/**
 * Data layer for `entry-level-299-month`. Thin typed fetch wrappers over /api/v1/entry-level-299-month.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface EntryLevel299MonthRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListEntryLevel299MonthResponse {
	count: number;
	items: EntryLevel299MonthRow[];
}

export interface CreateEntryLevel299MonthBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/entry-level-299-month";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListEntryLevel299MonthResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListEntryLevel299MonthResponse>(res);
}

export async function create(body: CreateEntryLevel299MonthBody): Promise<EntryLevel299MonthRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<EntryLevel299MonthRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const entryLevel299MonthQueryKeys = {
	all: ["entry-level-299-month"] as const,
	list: () => [...entryLevel299MonthQueryKeys.all] as const,
	detail: (id: string) => [...entryLevel299MonthQueryKeys.all, id] as const,
};
